import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { Badge, Button, Notice, Plate, SelectField, TextField, type ListColumn } from "@wilco/ui";
import { clientPaged, DocumentList, type ListQuery } from "../docs/DocumentList.tsx";
import { FormPage, useDocumentForm, type FieldErrors } from "../docs/DocumentForm.tsx";
import { formatDate, formatMoney, t } from "../i18n.ts";
import { errorNotice } from "../stock/ShelfPage.tsx";
import { parseQty } from "../stock/qty.ts";
import { BackHead, Fact } from "./BankDocs.tsx";
import { articlesQuery, docsApi, MONEY, moneyApi, today, type Article, type Entry, type Origin, type Rule, type Template } from "./api.ts";

/*
 * «Операції» «Витрат» (каркас #77): реестр с карточкой записи и строкой
 * P&L, статьи, правила разнесения и шаблоны со своими формами. Реестр
 * только добавляется: у записи карточка без правки (OPX-01).
 */

const ORIGINS: Origin[] = ["BANK", "EXPENSE", "MANUAL", "TEMPLATE"];
const ACTIVITIES = ["", "SER", "RND", "SRV"] as const;
const activityOptions = () =>
  ACTIVITIES.map((a) => ({
    value: a,
    label: a === "" ? t("opex.activity.none") : a,
  }));
const articleOptions = (articles: Article[] | undefined) => [
  { value: "", label: t("opex.chooseArticle") },
  ...(articles ?? []).map((a) => ({ value: a.code, label: a.name })),
];
const articleName = (articles: Article[] | undefined, code: string) => articles?.find((a) => a.code === code)?.name ?? code;
const analytics = (family: string, activity?: string | null) => [family, activity].filter(Boolean).join(" · ");
/** Источник записи словами; новый источник сервера, которого веб ещё не знает, кодом. */
const originLabel = (origin: string) => ((ORIGINS as string[]).includes(origin) ? t(`opex.origin.${origin as Origin}`) : origin);

/** Сумма из поля: запятая или точка, пробел тысяч. */
const amountOf = (raw: string): number | null => parseQty(raw);

// --- реестр ------------------------------------------------------------------

const entriesRoute = getRouteApi("/app/money/opex/entries");

export function EntriesListPage() {
  const search = entriesRoute.useSearch();
  const navigate = useNavigate();
  const columns: ListColumn<Entry>[] = [
    {
      key: "who",
      header: t("opex.counterparty"),
      kind: "title",
      render: (r) => r.counterparty,
    },
    {
      key: "pnl",
      header: t("money.pnlLine"),
      kind: "text",
      render: (r) => pnl(r),
    },
    {
      key: "analytics",
      header: t("opex.analytics"),
      kind: "text",
      width: "7rem",
      render: (r) => analytics(r.family, r.activity),
    },
    {
      key: "origin",
      header: t("opex.origin"),
      kind: "text",
      width: "8rem",
      render: (r) => <Badge>{originLabel(r.origin)}</Badge>,
    },
    {
      key: "date",
      header: t("bank.date"),
      kind: "text",
      width: "5rem",
      render: (r) => formatDate(r.date),
    },
    {
      key: "amount",
      header: t("bank.amount"),
      kind: "number",
      width: "9rem",
      render: (r) => formatMoney(r.amount, "UAH"),
    },
  ];
  return (
    <DocumentList
      title={t("ops.expense.entries")}
      icon="expense"
      queryKey={[...MONEY, "docs", "entries"]}
      load={({ q, status, cursor, limit }: ListQuery) => docsApi.entries(q, status, { cursor, limit })}
      columns={columns}
      rowKey={(r) => String(r.id)}
      onOpen={(r) =>
        void navigate({
          to: "/money/opex/entries/$id",
          params: { id: String(r.id) },
        })
      }
      statuses={ORIGINS.map((o) => ({
        value: o,
        label: t(`opex.origin.${o}`),
      }))}
      sorts={[{ value: "date", label: t("money.sort.newest") }]}
      search={search}
      onSearch={(next) =>
        void navigate({
          to: "/money/opex/entries",
          search: next,
          replace: true,
        })
      }
      searchHint={t("money.entries.searchHint")}
      create={{ label: t("opex.newEntry"), to: "/money/opex/entries/new" }}
      empty={{ title: t("money.entries.empty") }}
    />
  );
}

/** Строка P&L записи: «Витрати» себестоимости, статья, месяц. */
const pnl = (e: Entry) => t("money.pnl", { article: e.articleName, month: e.month });

const entryRoute = getRouteApi("/app/money/opex/entries/$id");

export function EntryPage() {
  const { id } = entryRoute.useParams();
  const entry = useQuery({
    queryKey: [...MONEY, "entry", id],
    queryFn: () => docsApi.entry(Number(id)),
  });
  const e = entry.data;
  return (
    <div className="page stock-narrow money">
      <BackHead to="/money/opex/entries" title={e ? e.counterparty : t("money.entry")} />
      {entry.error ? <Notice tone="danger" title={errorNotice(entry.error).title} /> : null}
      {e ? (
        <Plate cut="lg">
          <dl className="money-facts">
            <Fact label={t("bank.amount")}>
              <span className="w-num">{formatMoney(e.amount, "UAH")}</span>
            </Fact>
            <Fact label={t("bank.date")}>{formatDate(e.date)}</Fact>
            <Fact label={t("opex.article")}>{e.articleName}</Fact>
            <Fact label={t("money.pnlLine")}>{pnl(e)}</Fact>
            <Fact label={t("opex.analytics")}>{analytics(e.family, e.activity) || e.family}</Fact>
            {e.edrpou ? <Fact label={t("money.edrpou")}>{e.edrpou}</Fact> : null}
            {e.description ? <Fact label={t("opex.description")}>{e.description}</Fact> : null}
            <Fact label={t("opex.origin")}>
              <Badge>{originLabel(e.origin)}</Badge>
            </Fact>
          </dl>
          <p className="w-muted">{t("money.entryAppendOnly")}</p>
        </Plate>
      ) : null}
    </div>
  );
}

interface EntryValues {
  date: string;
  counterparty: string;
  article: string;
  amount: string;
  family: string;
  activity: string;
  description: string;
}

export function NewEntryPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const articles = useQuery(articlesQuery);
  const form = useDocumentForm<EntryValues, Entry>({
    initial: {
      date: today(),
      counterparty: "",
      article: "",
      amount: "",
      family: "",
      activity: "",
      description: "",
    },
    validate: (v) => ({
      date: v.date ? undefined : t("docs.required"),
      counterparty: v.counterparty.trim() ? undefined : t("docs.required"),
      article: v.article ? undefined : t("docs.required"),
      amount: amountOf(v.amount) === null ? t("opex.amountNotNumber") : undefined,
    }),
    submit: (v, key) => {
      const family = v.family.trim().toUpperCase();
      return docsApi.createEntry(
        {
          date: v.date,
          counterparty: v.counterparty,
          article: v.article,
          amount: amountOf(v.amount) ?? 0,
          families: family ? { [family]: 1 } : {},
          activity: v.activity || null,
          description: v.description,
        },
        key,
      );
    },
    onSuccess: (e) => {
      void queryClient.invalidateQueries({ queryKey: MONEY });
      void navigate({
        to: "/money/opex/entries/$id",
        params: { id: String(e.id) },
        replace: true,
      });
    },
    fieldOf: (code) => FIELD_OF[code] as keyof EntryValues | undefined,
  });
  const { values, set, errors } = form;
  return (
    <FormPage title={t("opex.newEntry")} back="/money/opex/entries" submitLabel={t("opex.addEntry")} form={form}>
      <TextField label={t("bank.date")} type="date" value={values.date} onChange={(e) => set("date", e.target.value)} error={errors.date} />
      <TextField label={t("opex.counterparty")} value={values.counterparty} onChange={(e) => set("counterparty", e.target.value)} error={errors.counterparty} />
      <SelectField
        label={t("opex.article")}
        value={values.article}
        onChange={(e) => set("article", e.target.value)}
        options={articleOptions(articles.data)}
        error={errors.article}
      />
      <TextField label={t("opex.amount")} inputMode="decimal" value={values.amount} onChange={(e) => set("amount", e.target.value)} error={errors.amount} />
      <AnalyticsFields values={values} set={set} errors={errors} />
      <TextField label={t("opex.description")} value={values.description} onChange={(e) => set("description", e.target.value)} />
    </FormPage>
  );
}

/** Отказ сервера на своём поле: коды opex к полям форм. */
const FIELD_OF: Record<string, string> = {
  "opex.amount_invalid": "amount",
  "opex.family_invalid": "family",
  "opex.split_forbidden": "family",
  "opex.activity_invalid": "activity",
  "opex.article_unknown": "article",
  "opex.article_exists": "code",
  "opex.rule_empty": "edrpou",
  "opex.template_invalid": "name",
};

interface WithAnalytics {
  family: string;
  activity: string;
}

/** Семейство одно (CST-20) и тип деятельности: общие поля записи, правила и шаблона. */
function AnalyticsFields<V extends WithAnalytics>({
  values,
  set,
  errors,
}: {
  values: V;
  set: <K extends keyof V>(k: K, v: V[K]) => void;
  errors: FieldErrors<V>;
}) {
  return (
    <>
      <TextField
        label={t("opex.family")}
        hint={t("opex.familyHint")}
        value={values.family}
        onChange={(e) => set("family", e.target.value as V["family"])}
        error={errors.family}
        autoComplete="off"
      />
      <SelectField
        label={t("opex.activity")}
        value={values.activity}
        onChange={(e) => set("activity", e.target.value as V["activity"])}
        options={activityOptions()}
        error={errors.activity}
      />
    </>
  );
}

// --- статьи ------------------------------------------------------------------

const articlesRoute = getRouteApi("/app/money/opex/articles");

export function ArticlesListPage() {
  const search = articlesRoute.useSearch();
  const navigate = useNavigate();
  const columns: ListColumn<Article>[] = [
    {
      key: "name",
      header: t("money.articleName"),
      kind: "title",
      render: (a) => a.name,
    },
    {
      key: "code",
      header: t("money.code"),
      kind: "text",
      width: "10rem",
      render: (a) => <span className="w-num">{a.code}</span>,
    },
    {
      key: "state",
      header: t("docs.status"),
      kind: "text",
      width: "8rem",
      render: (a) => (a.archived ? <Badge>{t("money.archived")}</Badge> : null),
    },
  ];
  return (
    <DocumentList
      title={t("ops.expense.articles")}
      icon="expense"
      queryKey={[...MONEY, "docs", "articles"]}
      load={({ q, status, cursor, limit }: ListQuery) => docsApi.articles(q, status === "archived", { cursor, limit })}
      columns={columns}
      rowKey={(a) => a.code}
      onOpen={(a) =>
        void navigate({
          to: "/money/opex/articles/$code",
          params: { code: a.code },
        })
      }
      statuses={[{ value: "archived", label: t("money.withArchived") }]}
      sorts={[{ value: "own", label: t("money.sort.own") }]}
      search={search}
      onSearch={(next) =>
        void navigate({
          to: "/money/opex/articles",
          search: next,
          replace: true,
        })
      }
      create={{ label: t("money.newArticle"), to: "/money/opex/articles/new" }}
      empty={{ title: t("money.articles.empty") }}
    />
  );
}

export function NewArticlePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const form = useDocumentForm<{ code: string; name: string }, Article>({
    initial: { code: "", name: "" },
    validate: (v) => ({
      code: v.code.trim() ? undefined : t("docs.required"),
      name: v.name.trim() ? undefined : t("docs.required"),
    }),
    submit: (v, key) => docsApi.createArticle({ code: v.code, name: v.name }, key),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: MONEY });
      void navigate({ to: "/money/opex/articles", search: {}, replace: true });
    },
    fieldOf: (code) => (FIELD_OF[code] === "code" || FIELD_OF[code] === "article" ? "code" : undefined),
  });
  const { values, set, errors } = form;
  return (
    <FormPage title={t("money.newArticle")} back="/money/opex/articles" submitLabel={t("money.create")} form={form}>
      <TextField
        label={t("money.code")}
        hint={t("money.codeHint")}
        value={values.code}
        onChange={(e) => set("code", e.target.value)}
        error={errors.code}
        autoComplete="off"
      />
      <TextField label={t("money.articleName")} value={values.name} onChange={(e) => set("name", e.target.value)} error={errors.name} />
    </FormPage>
  );
}

const articleRoute = getRouteApi("/app/money/opex/articles/$code");

/** Правка статьи: имя и архив. Код не правится: им размечены записи и правила. */
export function EditArticlePage() {
  const { code } = articleRoute.useParams();
  const all = useQuery({
    queryKey: [...MONEY, "articles", "all"],
    queryFn: () => docsApi.articles("", true, { cursor: null, limit: 200 }),
  });
  const article = all.data?.items.find((a) => a.code === code);
  if (all.error) return <Notice tone="danger" title={errorNotice(all.error).title} />;
  if (!all.data) return null;
  if (!article) return <Notice tone="danger" title={t("docs.notFound")} />;
  return <EditArticleForm article={article} />;
}

function EditArticleForm({ article }: { article: Article }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const form = useDocumentForm<{ name: string; archived: string }, Article>({
    initial: { name: article.name, archived: article.archived ? "yes" : "" },
    validate: (v) => ({ name: v.name.trim() ? undefined : t("docs.required") }),
    submit: (v, key) => docsApi.updateArticle(article.code, { name: v.name, archived: v.archived === "yes" }, key),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: MONEY });
      void navigate({ to: "/money/opex/articles", search: {}, replace: true });
    },
  });
  const { values, set, errors } = form;
  return (
    <FormPage title={article.name} back="/money/opex/articles" submitLabel={t("money.save")} form={form}>
      <p className="w-muted w-num">{article.code}</p>
      <TextField label={t("money.articleName")} value={values.name} onChange={(e) => set("name", e.target.value)} error={errors.name} />
      <SelectField
        label={t("docs.status")}
        value={values.archived}
        onChange={(e) => set("archived", e.target.value)}
        options={[
          { value: "", label: t("money.active") },
          { value: "yes", label: t("money.archived") },
        ]}
      />
      <p className="w-muted">{t("money.archiveHint")}</p>
    </FormPage>
  );
}

// --- правила -----------------------------------------------------------------

const rulesRoute = getRouteApi("/app/money/opex/rules");

// Правил десятки: поиск по ЄДРПОУ и слову в браузере по всем правилам в порядке применения.
const loadRules = clientPaged(moneyApi.rules, {
  text: (r) => `${r.edrpou ?? ""} ${r.purposeContains ?? ""} ${r.article} ${r.family}`,
  compare: { priority: () => 0 },
});

export function RulesListPage() {
  const search = rulesRoute.useSearch();
  const navigate = useNavigate();
  const articles = useQuery(articlesQuery);
  const columns: ListColumn<Rule>[] = [
    {
      key: "when",
      header: t("money.ruleWhen"),
      kind: "title",
      render: (r) => ruleWhen(r),
    },
    {
      key: "article",
      header: t("opex.article"),
      kind: "text",
      render: (r) => articleName(articles.data, r.article),
    },
    {
      key: "analytics",
      header: t("opex.analytics"),
      kind: "text",
      width: "7rem",
      render: (r) => analytics(r.family, r.activity),
    },
    {
      key: "priority",
      header: t("opex.priorityField"),
      kind: "number",
      width: "6rem",
      render: (r) => r.priority,
    },
  ];
  return (
    <DocumentList
      title={t("ops.expense.rules")}
      icon="expense"
      queryKey={[...MONEY, "docs", "rules"]}
      load={loadRules}
      columns={columns}
      rowKey={(r) => String(r.id)}
      onOpen={(r) =>
        void navigate({
          to: "/money/opex/rules/$id",
          params: { id: String(r.id) },
        })
      }
      sorts={[{ value: "priority", label: t("money.sort.priority") }]}
      search={search}
      onSearch={(next) => void navigate({ to: "/money/opex/rules", search: next, replace: true })}
      searchHint={t("money.rules.searchHint")}
      create={{ label: t("opex.newRule"), to: "/money/opex/rules/new" }}
      empty={{ title: t("opex.rules.empty"), body: t("opex.rules.lead") }}
    />
  );
}

const ruleWhen = (r: Rule) =>
  [r.edrpou ? t("bank.edrpou", { code: r.edrpou }) : null, r.purposeContains ? t("opex.purposeHas", { word: r.purposeContains }) : null]
    .filter(Boolean)
    .join(", ");

interface RuleValues {
  priority: string;
  edrpou: string;
  purposeContains: string;
  article: string;
  family: string;
  activity: string;
}

const ruleValues = (r?: Rule): RuleValues => ({
  priority: String(r?.priority ?? 100),
  edrpou: r?.edrpou ?? "",
  purposeContains: r?.purposeContains ?? "",
  article: r?.article ?? "",
  family: r && r.family !== "ZAG" ? r.family : "",
  activity: r?.activity ?? "",
});

const validateRule = (v: RuleValues): FieldErrors<RuleValues> => ({
  priority: /^\d+$/.test(v.priority.trim()) ? undefined : t("money.priorityNotNumber"),
  edrpou: v.edrpou.trim() || v.purposeContains.trim() ? undefined : t("money.ruleNeedsCondition"),
  article: v.article ? undefined : t("docs.required"),
});

const ruleBody = (v: RuleValues) => ({
  priority: Number(v.priority.trim()),
  edrpou: v.edrpou.trim() || null,
  purposeContains: v.purposeContains.trim() || null,
  article: v.article,
  family: v.family.trim().toUpperCase() || null,
  activity: v.activity || null,
});

function RuleForm({ rule, title }: { rule?: Rule; title: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const articles = useQuery(articlesQuery);
  const form = useDocumentForm<RuleValues, Rule>({
    initial: ruleValues(rule),
    validate: validateRule,
    submit: (v, key) => (rule ? docsApi.updateRule(rule.id, ruleBody(v), key) : docsApi.createRule(ruleBody(v), key)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: MONEY });
      void navigate({ to: "/money/opex/rules", search: {}, replace: true });
    },
    fieldOf: (code) => FIELD_OF[code] as keyof RuleValues | undefined,
  });
  const { values, set, errors } = form;
  return (
    <FormPage title={title} back="/money/opex/rules" submitLabel={rule ? t("money.save") : t("opex.addRule")} form={form}>
      <p className="w-muted">{t("opex.rules.lead")}</p>
      <TextField label={t("opex.ruleEdrpou")} inputMode="numeric" value={values.edrpou} onChange={(e) => set("edrpou", e.target.value)} error={errors.edrpou} />
      <TextField label={t("opex.rulePurpose")} value={values.purposeContains} onChange={(e) => set("purposeContains", e.target.value)} />
      <SelectField
        label={t("opex.article")}
        value={values.article}
        onChange={(e) => set("article", e.target.value)}
        options={articleOptions(articles.data)}
        error={errors.article}
      />
      <AnalyticsFields values={values} set={set} errors={errors} />
      <TextField
        label={t("opex.priorityField")}
        inputMode="numeric"
        value={values.priority}
        onChange={(e) => set("priority", e.target.value)}
        error={errors.priority}
      />
    </FormPage>
  );
}

export function NewRulePage() {
  return <RuleForm title={t("opex.newRule")} />;
}

const ruleRoute = getRouteApi("/app/money/opex/rules/$id");

export function EditRulePage() {
  const { id } = ruleRoute.useParams();
  const rules = useQuery({
    queryKey: [...MONEY, "rules"],
    queryFn: moneyApi.rules,
  });
  const rule = rules.data?.find((r) => String(r.id) === id);
  if (rules.error) return <Notice tone="danger" title={errorNotice(rules.error).title} />;
  if (!rules.data) return null;
  if (!rule) return <Notice tone="danger" title={t("docs.notFound")} />;
  return <RuleForm rule={rule} title={ruleWhen(rule)} />;
}

// --- шаблоны -----------------------------------------------------------------

const templatesRoute = getRouteApi("/app/money/opex/templates");

export function TemplatesListPage() {
  const search = templatesRoute.useSearch();
  const navigate = useNavigate();
  const articles = useQuery(articlesQuery);
  const columns: ListColumn<Template>[] = [
    {
      key: "name",
      header: t("opex.templateName"),
      kind: "title",
      render: (r) => r.name,
    },
    {
      key: "article",
      header: t("opex.article"),
      kind: "text",
      render: (r) => articleName(articles.data, r.article),
    },
    {
      key: "when",
      header: t("money.when"),
      kind: "text",
      width: "10rem",
      render: (r) => t("opex.everyMonth", { day: r.dayOfMonth }),
    },
    {
      key: "state",
      header: t("docs.status"),
      kind: "text",
      width: "8rem",
      render: (r) => (r.active ? null : <Badge>{t("opex.stopped")}</Badge>),
    },
    {
      key: "amount",
      header: t("bank.amount"),
      kind: "number",
      width: "9rem",
      render: (r) => formatMoney(r.amount, "UAH"),
    },
  ];
  return (
    <DocumentList
      title={t("ops.expense.templates")}
      icon="expense"
      queryKey={[...MONEY, "docs", "templates"]}
      load={async ({ q, status, cursor, limit }: ListQuery) => {
        const page = await docsApi.templates(status === null ? null : status === "active", { cursor, limit });
        const needle = q.toLocaleLowerCase("uk");
        return needle
          ? {
              ...page,
              items: page.items.filter((r) => `${r.name} ${r.counterparty}`.toLocaleLowerCase("uk").includes(needle)),
            }
          : page;
      }}
      columns={columns}
      rowKey={(r) => String(r.id)}
      onOpen={(r) =>
        void navigate({
          to: "/money/opex/templates/$id",
          params: { id: String(r.id) },
        })
      }
      statuses={[
        { value: "active", label: t("money.active") },
        { value: "stopped", label: t("opex.stopped") },
      ]}
      sorts={[{ value: "name", label: t("money.sort.name") }]}
      search={search}
      onSearch={(next) =>
        void navigate({
          to: "/money/opex/templates",
          search: next,
          replace: true,
        })
      }
      create={{ label: t("opex.newTemplate"), to: "/money/opex/templates/new" }}
      empty={{
        title: t("opex.templates.empty"),
        body: t("opex.templates.lead"),
      }}
    />
  );
}

interface TemplateValues {
  name: string;
  article: string;
  amount: string;
  counterparty: string;
  dayOfMonth: string;
  fromMonth: string;
  family: string;
  activity: string;
}

const templateValues = (r?: Template): TemplateValues => ({
  name: r?.name ?? "",
  article: r?.article ?? "",
  amount: r ? String(r.amount).replace(".", ",") : "",
  counterparty: r?.counterparty ?? "",
  dayOfMonth: String(r?.dayOfMonth ?? 1),
  fromMonth: r?.fromMonth ?? today().slice(0, 7),
  family: r && r.family !== "ZAG" ? r.family : "",
  activity: r?.activity ?? "",
});

const validateTemplate = (v: TemplateValues): FieldErrors<TemplateValues> => {
  const day = Number(v.dayOfMonth);
  return {
    name: v.name.trim() ? undefined : t("docs.required"),
    article: v.article ? undefined : t("docs.required"),
    amount: amountOf(v.amount) === null ? t("opex.amountNotNumber") : undefined,
    counterparty: v.counterparty.trim() ? undefined : t("docs.required"),
    dayOfMonth: Number.isInteger(day) && day >= 1 && day <= 31 ? undefined : t("money.dayOfMonthRange"),
    fromMonth: v.fromMonth ? undefined : t("docs.required"),
  };
};

const templateBody = (v: TemplateValues) => ({
  name: v.name,
  article: v.article,
  amount: amountOf(v.amount) ?? 0,
  counterparty: v.counterparty,
  dayOfMonth: Number(v.dayOfMonth),
  fromMonth: v.fromMonth,
  family: v.family.trim().toUpperCase() || null,
  activity: v.activity || null,
});

function TemplateForm({ template, title }: { template?: Template; title: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const articles = useQuery(articlesQuery);
  const form = useDocumentForm<TemplateValues, Template>({
    initial: templateValues(template),
    validate: validateTemplate,
    submit: (v, key) => (template ? docsApi.updateTemplate(template.id, templateBody(v), key) : docsApi.createTemplate(templateBody(v), key)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: MONEY });
      void navigate({ to: "/money/opex/templates", search: {}, replace: true });
    },
    fieldOf: (code) => FIELD_OF[code] as keyof TemplateValues | undefined,
  });
  const { values, set, errors } = form;
  return (
    <FormPage title={title} back="/money/opex/templates" submitLabel={template ? t("money.save") : t("opex.addTemplate")} form={form}>
      {template ? <p className="w-muted">{t("money.templateEditHint")}</p> : <p className="w-muted">{t("opex.templates.lead")}</p>}
      <TextField label={t("opex.templateName")} value={values.name} onChange={(e) => set("name", e.target.value)} error={errors.name} />
      <TextField label={t("opex.counterparty")} value={values.counterparty} onChange={(e) => set("counterparty", e.target.value)} error={errors.counterparty} />
      <SelectField
        label={t("opex.article")}
        value={values.article}
        onChange={(e) => set("article", e.target.value)}
        options={articleOptions(articles.data)}
        error={errors.article}
      />
      <TextField label={t("opex.amount")} inputMode="decimal" value={values.amount} onChange={(e) => set("amount", e.target.value)} error={errors.amount} />
      <TextField
        label={t("opex.dayOfMonth")}
        inputMode="numeric"
        value={values.dayOfMonth}
        onChange={(e) => set("dayOfMonth", e.target.value)}
        error={errors.dayOfMonth}
      />
      <TextField
        label={t("opex.fromMonth")}
        type="month"
        value={values.fromMonth}
        onChange={(e) => set("fromMonth", e.target.value)}
        error={errors.fromMonth}
      />
      <AnalyticsFields values={values} set={set} errors={errors} />
    </FormPage>
  );
}

export function NewTemplatePage() {
  return <TemplateForm title={t("opex.newTemplate")} />;
}

const templateRoute = getRouteApi("/app/money/opex/templates/$id");

export function EditTemplatePage() {
  const { id } = templateRoute.useParams();
  const all = useQuery({
    queryKey: [...MONEY, "templates"],
    queryFn: moneyApi.templates,
  });
  const template = all.data?.find((r) => String(r.id) === id);
  if (all.error) return <Notice tone="danger" title={errorNotice(all.error).title} />;
  if (!all.data) return null;
  if (!template) return <Notice tone="danger" title={t("docs.notFound")} />;
  return (
    <>
      <TemplateForm template={template} title={template.name} />
      {template.active ? <StopTemplate id={template.id} /> : null}
    </>
  );
}

/** «Зупинити» под формой: шаблон перестаёт писать записи, сделанные остаются. */
function StopTemplate({ id }: { id: number }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return (
    <div className="page stock-narrow">
      <Button
        variant="ghost"
        onClick={() =>
          void moneyApi.stopTemplate(id).then(() => {
            void queryClient.invalidateQueries({ queryKey: MONEY });
            void navigate({
              to: "/money/opex/templates",
              search: {},
              replace: true,
            });
          })
        }
      >
        {t("opex.stopTemplate")}
      </Button>
    </div>
  );
}
